/**
 * Searches, with what was typed in the request BODY, never the URL (CLA-1694).
 *
 * A clinician's search is usually a patient's name, sometimes a mobile or a
 * date of birth. In a query string it would land in browser history, edge and
 * proxy logs, error reports and the `Referer` of the next request. So every
 * search that can carry patient details goes through here, as a POST to the
 * backend's body form (claro-elixir `ClaroWeb.SearchBody`). The GET forms
 * still exist for shipped mobile builds; the web never calls them.
 *
 * Rule for new code: search text, names, mobiles, emails and dates of birth
 * never go in a path or query string. Add a body search here instead.
 * `search.guard.test.ts` fails on the old pattern.
 */
import { api } from '@/api/client'
import type { components } from '@/api/schema'
import type {
  AssistResolveRequest,
  AssistResolveResponse,
  NoteListResponse,
  PatientDuplicates,
  PatientListResponse,
} from '@/api/types'

/** Drop empty values so the body carries only what was asked. */
function compact<T extends Record<string, unknown>>(body: T): Partial<T> {
  const out: Partial<T> = {}
  for (const [k, v] of Object.entries(body)) {
    if (v !== undefined && v !== null && v !== '') (out as Record<string, unknown>)[k] = v
  }
  return out
}

export interface PatientSearchBody {
  q?: string
  limit?: number
  cursor?: string
  clinic_id?: number | null
  /** `all` for the platform admin list. */
  scope?: 'all'
}

/** `GET /patients` in body form: the patient list, optionally searched. */
export function searchPatients<T = PatientListResponse>(body: PatientSearchBody): Promise<T> {
  return api.post<T>('/patients/search', compact({ ...body }))
}

export interface DuplicateProbeBody {
  first_name?: string
  last_name?: string
  dob?: string
  email?: string
  phone?: string
}

/** The create-patient duplicate hint (CLA-1244), details in the body. */
export function findPatientDuplicates(probe: DuplicateProbeBody): Promise<PatientDuplicates> {
  return api.post<PatientDuplicates>('/patients/duplicates', compact({ ...probe }))
}

export interface NoteSearchBody {
  q?: string
  limit?: number
  clinic_id?: number | null
  patient_uuid?: string
  created_after?: string
  created_before?: string
  /** Comma-separated label slugs. */
  labels?: string
  before_created_at?: string
  before_id?: number
  view?: string
}

/** `GET /notes` in body form: the caller's notes, optionally searched. */
export function searchNotes<T = NoteListResponse>(body: NoteSearchBody): Promise<T> {
  return api.post<T>('/notes/search', compact({ ...body }))
}

/** Look a patient up in the clinic's connected EMR. */
export function searchEmr<T>(body: { name: string; dob?: string }): Promise<T> {
  return api.post<T>('/emr/search', compact({ ...body }))
}

/** Patients to start a messaging thread about. */
export function searchMessagingPatients<T>(q: string): Promise<T> {
  return api.post<T>('/messaging/patients/search', { q })
}

/** Messages in the caller's conversations. */
export function searchMessages<T>(q: string): Promise<T> {
  return api.post<T>('/messaging/search', { q })
}

/** Recent appointments to link a note to, by patient name or patient. */
export function searchAppointments<T>(body: { q?: string; patient_uuid?: string }): Promise<T> {
  return api.post<T>('/calendar/appointments/search', compact({ ...body }))
}

/**
 * "Search or ask" (CLA-1805): what was typed, ranked against Claro's actions
 * into cards. Only when `GET /settings` says `assist_available`; otherwise the
 * route is a 404. `signal` cancels a request that typing has superseded.
 */
export function resolveAssist(
  body: AssistResolveRequest,
  opts?: { signal?: AbortSignal },
): Promise<AssistResolveResponse> {
  return api.post<AssistResolveResponse>('/assist/resolve', body, opts)
}

/**
 * Full-text search over this realm's help articles, bodies included, filtered
 * to the caller's role (claro-elixir `POST /help/search`). What is typed in
 * ⌘K is often a patient's name, so it goes in the body like the others.
 */
export function searchHelp(
  body: { q: string; limit?: number },
  opts?: { signal?: AbortSignal },
): Promise<components['schemas']['HelpSearchResponse']> {
  return api.post('/help/search', compact({ ...body }), opts)
}
